/**
 * pinCrypto.js — Sprint 1.5b.1
 *
 * Fix: tutte le costanti libsodium sono lette DOPO sodium.ready,
 * non al caricamento modulo. Risolve "keyLength cannot be null or undefined".
 */

import sodium from 'libsodium-wrappers-sumo';

let _ready = false;
let _consts = null; // popolato dopo sodium.ready

async function ready() {
  if (_ready) return _consts;
  await sodium.ready;
  _ready = true;
  _consts = {
    // MODERATE invece di INTERACTIVE: il blob PIN-cifrato vive in localStorage
    // ed e' brute-forceable OFFLINE da chi ha il device. INTERACTIVE (~0.1s per
    // tentativo) e' troppo veloce. MODERATE (~1s, ~256MB) alza il costo di ~10x.
    // Il contatore tentativi in localStorage NON e' una vera difesa (DevTools
    // puo' resettarlo): la difesa vera e' la work-factor di Argon2id + la
    // qualita' del PIN.
    OPSLIMIT:    sodium.crypto_pwhash_OPSLIMIT_MODERATE,
    MEMLIMIT:    sodium.crypto_pwhash_MEMLIMIT_MODERATE,
    ALG:         sodium.crypto_pwhash_ALG_ARGON2ID13,
    KEY_LEN:     sodium.crypto_secretbox_KEYBYTES,    // 32
    NONCE_LEN:   sodium.crypto_secretbox_NONCEBYTES,  // 24
    SALT_LEN:    sodium.crypto_pwhash_SALTBYTES,      // 16
  };
  return _consts;
}

// ---- base64url helpers ----
function bufToB64Url(buf) {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function b64UrlToBuf(s) {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ---- PIN validation ----
export function isValidPin(pin) {
  return typeof pin === 'string' && /^[0-9]{6}$/.test(pin);
}
export function isPinStrong(pin) {
  if (!isValidPin(pin)) return false;
  if (/^(\d)\1{5}$/.test(pin)) return false;
  const seqs = ['012345', '123456', '234567', '345678', '456789',
                '987654', '876543', '765432', '654321', '543210'];
  if (seqs.includes(pin)) return false;
  const blacklist = ['000000','111111','222222','333333','444444',
                     '555555','666666','777777','888888','999999',
                     '121212','123123','696969','420420'];
  if (blacklist.includes(pin)) return false;
  return true;
}

// ---- Passphrase validation (Premium) ----
// Una passphrase alfanumerica sostituisce il PIN di 6 cifre come segreto che
// protegge il backup. La work-factor e' la stessa (Argon2id MODERATE); il
// vantaggio e' l'entropia molto piu' alta dell'input → brute-force impraticabile.
// Regola: 8–128 caratteri, e almeno 2 classi di caratteri OPPURE lunghezza >= 12
// (cosi' "correcthorsebattery" passa via lunghezza, "abc12345" via 2 classi).
export function isValidPassphrase(s) {
  if (typeof s !== 'string') return false;
  if (s.length < 8 || s.length > 128) return false;
  let classes = 0;
  if (/[a-z]/i.test(s)) classes++;
  if (/[0-9]/.test(s)) classes++;
  if (/[^A-Za-z0-9]/.test(s)) classes++;
  return s.length >= 12 || classes >= 2;
}
// Indicatore qualitativo per la UI (meter): 'invalid' | 'weak' | 'ok' | 'strong'.
export function passphraseStrength(s) {
  if (!isValidPassphrase(s)) return 'invalid';
  let classes = 0;
  if (/[a-z]/.test(s)) classes++;
  if (/[A-Z]/.test(s)) classes++;
  if (/[0-9]/.test(s)) classes++;
  if (/[^A-Za-z0-9]/.test(s)) classes++;
  if (s.length >= 16 && classes >= 3) return 'strong';
  if (s.length >= 12 && classes >= 2) return 'ok';
  if (s.length >= 14) return 'ok';
  return 'weak';
}

// ---- key derivation ----
// `params` (opzionale) per re-derivare con i parametri Argon2id originali
// di un blob esistente (cosi' i blob piu' vecchi cifrati con INTERACTIVE
// continuano a decifrare anche dopo l'upgrade a MODERATE).
export async function deriveKey(pin, sigilLower, salt, params = null) {
  const C = await ready();
  // Accetta un PIN (6 cifre) OPPURE una passphrase alfanumerica (Premium):
  // il segreto e' comunque una stringa passata ad Argon2id.
  if (!isValidPin(pin) && !isValidPassphrase(pin)) throw new Error('invalid secret format');
  if (!sigilLower) throw new Error('missing sigilLower');
  if (!(salt instanceof Uint8Array) || salt.length !== C.SALT_LEN) {
    throw new Error(`salt must be ${C.SALT_LEN} bytes`);
  }
  const password = `${pin}::${sigilLower}`;
  const opslimit = params?.opslimit || C.OPSLIMIT;
  const memlimit = params?.memlimit || C.MEMLIMIT;
  const alg = params?.alg || C.ALG;
  return sodium.crypto_pwhash(
    C.KEY_LEN, password, salt, opslimit, memlimit, alg
  );
}

// ---- encrypt ----
export async function encryptMnemonic({ pin, sigilLower, mnemonic }) {
  const C = await ready();
  if (typeof mnemonic !== 'string' || mnemonic.length === 0) {
    throw new Error('mnemonic required');
  }
  const salt = sodium.randombytes_buf(C.SALT_LEN);
  const key = await deriveKey(pin, sigilLower, salt);
  const nonce = sodium.randombytes_buf(C.NONCE_LEN);
  const plaintext = sodium.from_string(mnemonic);
  const ciphertext = sodium.crypto_secretbox_easy(plaintext, nonce, key);
  for (let i = 0; i < key.length; i++) key[i] = 0;
  return {
    blob:  bufToB64Url(ciphertext),
    salt:  bufToB64Url(salt),
    nonce: bufToB64Url(nonce),
    argon2: {
      type: 'id',
      opslimit: C.OPSLIMIT,
      memlimit: C.MEMLIMIT,
      alg: C.ALG,
    },
  };
}

// ---- decrypt ----
// Accetta `argon2` (metadata salvato nel blob) per re-derivare con i parametri
// con cui era stato cifrato in origine: cosi' i blob INTERACTIVE creati prima
// dell'upgrade MODERATE continuano a decifrare correttamente.
export async function decryptMnemonic({ pin, sigilLower, blob, salt, nonce, argon2 = null }) {
  await ready();
  if (!blob || !salt || !nonce) throw new Error('blob/salt/nonce required');
  const saltBuf = b64UrlToBuf(salt);
  const nonceBuf = b64UrlToBuf(nonce);
  const blobBuf = b64UrlToBuf(blob);
  const key = await deriveKey(pin, sigilLower, saltBuf, argon2);
  try {
    const plain = sodium.crypto_secretbox_open_easy(blobBuf, nonceBuf, key);
    for (let i = 0; i < key.length; i++) key[i] = 0;
    return sodium.to_string(plain);
  } catch {
    for (let i = 0; i < key.length; i++) key[i] = 0;
    return null;
  }
}

// ---- localStorage persistence ----
//
// IMPORTANTE: NON salviamo piu' il blob cifrato in localStorage. Il blob e'
// solo sul server (rate-limit 10/h, 30/24h per sigil). Localmente teniamo
// solo il sigil per ricordare "questo device ha gia' un account, vai a
// /pin-unlock". Cosi' chi acceda al device + DevTools NON trova un blob
// estraibile da brute-forzare offline fuori dal browser.
//
// Backward-compat: i client vecchi (pre-fix) hanno il blob nel vecchio
// LS_KEY. La migrazione (vedi hasLocalBackup) lo wipa al primo accesso.
const LS_KEY = 'nullum.pinBackup.v1';        // LEGACY (verra' wipato)
const LS_SIGIL_KEY = 'nullum.sigil.v1';      // sigil dell'account su questo device

export function saveLastSigil(sigil) {
  if (!sigil || typeof sigil !== 'string') return;
  try { localStorage.setItem(LS_SIGIL_KEY, sigil); } catch {}
}
export function loadLastSigil() {
  try { return localStorage.getItem(LS_SIGIL_KEY) || null; } catch { return null; }
}
export function clearLastSigil() {
  try { localStorage.removeItem(LS_SIGIL_KEY); } catch {}
}

// ---- Catalogo multi-identità (v1.5) -------------------------------------
// Il device può ospitare PIÙ identità (sigil). `nullum.sigil.v1` resta il sigil
// ATTIVO (quello che /pin-unlock sblocca); il catalogo elenca TUTTI i sigil del
// device per poterli scegliere. Il blob cifrato di ciascuno vive sul server
// per-identità: il catalogo NON contiene segreti, solo nomi + flag.
const LS_ACCOUNTS_KEY = 'nullum.accounts.v1'; // [{ sigil, ts, tor }]
const MAX_ACCOUNTS = 12;

export function loadAccounts() {
  let arr = [];
  try { arr = JSON.parse(localStorage.getItem(LS_ACCOUNTS_KEY) || '[]'); } catch {}
  if (!Array.isArray(arr)) arr = [];
  arr = arr.filter((a) => a && typeof a.sigil === 'string' && a.sigil);
  // Retrocompat: device pre-v1.5 ha solo il sigil attivo legacy, nessun catalogo
  // → derivalo, così lo switcher vede almeno l'identità esistente.
  if (arr.length === 0) {
    const legacy = loadLastSigil();
    if (legacy) arr = [{ sigil: legacy, ts: 0, tor: false }];
  }
  return arr;
}
export function saveAccounts(arr) {
  try {
    const clean = (Array.isArray(arr) ? arr : []).filter((a) => a && a.sigil).slice(0, MAX_ACCOUNTS);
    localStorage.setItem(LS_ACCOUNTS_KEY, JSON.stringify(clean));
  } catch {}
}
/** Aggiunge (o aggiorna) un'identità nel catalogo. Dedup per sigil (case-insensitive),
 *  la più recente in testa. NON tocca il sigil attivo. */
export function addAccount(sigil, { tor = false, disposable = false } = {}) {
  if (!sigil || typeof sigil !== 'string') return;
  const lower = sigil.toLowerCase();
  const rest = loadAccounts().filter((a) => a.sigil.toLowerCase() !== lower);
  saveAccounts([{ sigil, ts: Date.now(), tor: !!tor, ...(disposable ? { disposable: true } : {}) }, ...rest]);
}
export function removeAccount(sigil) {
  if (!sigil) return;
  const lower = sigil.toLowerCase();
  saveAccounts(loadAccounts().filter((a) => a.sigil.toLowerCase() !== lower));
}
/** Sigil attualmente attivo sul device (quello che /pin-unlock sblocca). */
export function getActiveSigil() { return loadLastSigil(); }
/** Imposta quale identità è attiva (prima di andare a /pin-unlock). */
export function setActiveSigil(sigil) { saveLastSigil(sigil); }
/** Vero se il device ha più di un'identità (→ mostra il selettore al boot). */
export function hasMultipleAccounts() { return loadAccounts().length > 1; }

// ---- Unlock mode (PIN vs passphrase), per-sigil ----
// Flag LOCALE che decide solo il DEFAULT dell'input di sblocco (tastierino vs
// campo testo). NON e' una dipendenza di correttezza: il decrypt funziona con
// qualunque segreto giusto, quindi un device senza il flag (es. nuovo, dopo
// restore) puo' comunque sbloccare scegliendo "passphrase" a mano. 'pin' e' il
// default e non scrive nulla (assenza di chiave = pin).
const LS_UNLOCKMODE_PREFIX = 'nullum.unlockMode.'; // + sigilLower → 'passphrase'
export function saveUnlockMode(sigil, mode) {
  const lower = (sigil || '').toLowerCase();
  if (!lower) return;
  try {
    if (mode === 'passphrase') localStorage.setItem(LS_UNLOCKMODE_PREFIX + lower, 'passphrase');
    else localStorage.removeItem(LS_UNLOCKMODE_PREFIX + lower);
  } catch {}
}
export function loadUnlockMode(sigil) {
  const lower = (sigil || '').toLowerCase();
  if (!lower) return 'pin';
  try {
    return localStorage.getItem(LS_UNLOCKMODE_PREFIX + lower) === 'passphrase'
      ? 'passphrase' : 'pin';
  } catch { return 'pin'; }
}

// Helpers LEGACY: leggono/cancellano il vecchio blob. Mai piu' chiamare
// saveLocalBackup nelle screen nuove — non viene piu' esportata.
export function loadLocalBackup() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== 'object') return null;
    if (!obj.blob || !obj.salt || !obj.nonce) return null;
    return obj;
  } catch { return null; }
}
export function clearLocalBackup() {
  try { localStorage.removeItem(LS_KEY); } catch {}
}

// `hasLocalBackup` ora controlla la presenza del SIGIL locale (non del blob).
// Side-effect: se trova un blob legacy senza sigil salvato, MIGRA (estrae il
// sigil, lo salva, e WIPA il blob — niente piu' blob estraibile offline).
export function hasLocalBackup() {
  if (loadLastSigil()) return true;
  const legacy = loadLocalBackup();
  if (legacy && legacy.sigil) {
    saveLastSigil(legacy.sigil);
    clearLocalBackup();
    return true;
  }
  return false;
}

// ---- local attempts counter ----
const ATTEMPTS_KEY = 'nullum.pinAttempts.v1';
const MAX_LOCAL_ATTEMPTS = 5;
export function getLocalAttempts() {
  try { return parseInt(localStorage.getItem(ATTEMPTS_KEY) || '0', 10) || 0; }
  catch { return 0; }
}
export function incLocalAttempts() {
  try {
    const cur = getLocalAttempts();
    const next = cur + 1;
    localStorage.setItem(ATTEMPTS_KEY, String(next));
    // Wipe del blob al raggiungimento del max: deterrente vs brute-force
    // locale. NB: non e' a prova di "attaccante consapevole" (puo' copiare
    // il blob da localStorage PRIMA di tentare, poi ripristinarlo dopo il
    // wipe). La difesa crittografica vera resta la work-factor Argon2id.
    if (next >= MAX_LOCAL_ATTEMPTS) {
      try { localStorage.removeItem(LS_KEY); } catch {}
    }
    return next;
  } catch { return 0; }
}
export function resetLocalAttempts() {
  try { localStorage.removeItem(ATTEMPTS_KEY); } catch {}
}
export function isLocallyLockedOut() {
  return getLocalAttempts() >= MAX_LOCAL_ATTEMPTS;
}
export { MAX_LOCAL_ATTEMPTS };
